import { action, get } from "@ember/object"
import { later } from "@ember/runloop"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import type { Group } from "@shared/models/group"
import type { UserSummary } from "@shared/models/user"
import FindManyAltogether from "cc-frontend/helpers/find-many-altogether"
import LiveQueryResource from "cc-frontend/helpers/live-query"
import type ModalManager from "cc-frontend/services/modal-manager"
import type NotifierService from "cc-frontend/services/notifier"
import type Rpc from "cc-frontend/services/rpc"
import type { group_add_individual_rpc_response } from "cc-frontend/services/rpc"
import { isGroupMembershipIndividual, membersWithAdminRole } from "cc-frontend/utils/group"
import parseEmails from "cc-frontend/utils/parse-emails"
import { use } from "ember-could-get-used-to-this"
import { first, forEach, isNil, map } from "lodash-es"
import * as R from "remeda" // tree-shaking supported!

export interface AddMembersInterface {
  Args: {
    order: number
    model: {
      group: Group
      initiallyOpened?: boolean
      makeAdmin?: boolean
      showOnlyAdmins?: boolean
    }
    closeModal: () => {}
  }
}

export default class AddMembers extends Component<AddMembersInterface> {
  @service declare modalManager: ModalManager
  @service declare rpc: Rpc
  @service declare notifier: NotifierService

  @use userQuery = new LiveQueryResource(() => [
    "invitable_users_for_group",
    this.args.model.group.id,
  ])
  @use userSummaries = new FindManyAltogether(() => ["user-summary", this.userIds])

  get teamMembers() {
    let showOnlyAdmins = this.args.model.showOnlyAdmins || false
    return R.pipe(
      get(this.args.model.group.attributes, "memberships"),
      R.filter(isGroupMembershipIndividual),
      R.filter((membership) => {
        return showOnlyAdmins ? membersWithAdminRole(membership) : true
      }),
      R.sortBy((membership) => membership.attributes.membershipCreatedAt),
      R.reverse()
    )
  }

  get userIds() {
    return map(this.userQuery.results, (result) => result.id)
  }

  @action optionallyOpened() {
    later(() => {
      if (this.args.model.initiallyOpened) {
        $(".add-member-dropdown__trigger input").focus()
      }
    }, 1000)
  }

  @action selectPerson(users: Array<UserSummary>) {
    let user = first(users)
    if (isNil(user)) return
    this.rpc
      .group_add_individual({
        groupId: this.args.model.group.id,
        email: user.attributes.email,
        makeAdmin: this.args.model.makeAdmin || false,
      })
      .then((response) => {
        if (isNil(user)) return
        this._handleCreateResponse(user.attributes.email, response)
      })
  }

  @action
  createPerson(term, _select) {
    forEach(parseEmails(term), (email) => {
      this.rpc
        .group_add_individual({
          groupId: this.args.model.group.id,
          email: email,
          makeAdmin: this.args.model.makeAdmin || false,
        })
        .then((response) => this._handleCreateResponse(email, response))
    })
  }

  private _handleCreateResponse(email: string, response: group_add_individual_rpc_response) {
    console.log("response", response)
    if (response.responseOk === true) {
      this.notifier.success("", {
        title: `${email} was invited!`,
        message: "We're sending them an email right now.",
        duration: 3000,
      })
    } else {
      if (response.result.code === "USER_ALREADY_ADMIN") {
        this.notifier.warning("", {
          title: `${email} was already invited!`,
          message: "They should have an email in their inbox",
          duration: 3000,
        })
      }
      if (response.result.code === "USER_ALREADY_MEMBER") {
        this.notifier.warning("", {
          title: `${email} was already invited!`,
          message: "They should have an email in their inbox",
          duration: 3000,
        })
      }
    }
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Group::AddMembers": typeof AddMembers
  }
}
